import {combineLatest}                         from 'rxjs';
import {
  ApDynformsConfigComboBox
}                                              from '../../../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {
  ApDynformsConfigFieldset
}                                              from '../../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  nameOf
}                                              from '../../../../../ap-core/utils/ap-name-of';
import {
  MachineStore
}                                              from '../../../../../stores/docu/machine.store';
import {
  InstrumentStore
}                                              from '../../../../../stores/docu/instrument.store';
import {
  DriverStore
}                                              from '../../../../../stores/docu/driver.store';
import {
  ApDynformsConfigTextbox
}                                              from '../../../../../ap-dynforms/config/ap-dynforms-config-textbox';
import {
  IPpPlanningWizardDetailsFormData,
  IPpPlanningWizardDetailsResponsiveData,
  IProductOption,
  PpConstants
}                                              from '../../pp-planning-wizard.types';
import {
  ApDynformsValidator
}                                              from '../../../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                            from '@angular/forms';
import {
  ApDynformsConfigDatepicker
}                                              from '../../../../../ap-dynforms/config/ap-dynforms-config-datepicker';
import {
  ApDynformsConfigTemplate
}                                              from '../../../../../ap-dynforms/config/ap-dynforms-config-template';
import {
  ApDynformsConfigPlaceholder
}                                              from '../../../../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {
  ApDynformsConfigNumerictextbox
}                                              from '../../../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {
  ApDynformsConfigButton
}                                              from '../../../../../ap-dynforms/config/ap-dynforms-config-button';
import {
  CampaignYearStore
}                                              from '../../../../../stores/login/campaignyear.store';
import {EventEmitter, Injectable, TemplateRef} from '@angular/core';
import {delay, map}                            from 'rxjs/operators';
import {
  ApTranslationService
}                                              from '../../../../../ap-utils/service/ap-translation.service';
import {
  ApDateService
}                                              from '../../../../../ap-core/services/ap-date-service';
import {
  OperationModesEnum
}                                              from '../../../../../stores/common/operation-modes.store';
import {
  IOperationModeAppSetting
}                                              from '../../../../../types/application-settings.types';
import {AgriportConstantsEnum}                 from '../../../../../ap-interface/enums/ap-agriport-constants.enum';
import {AgriportConstantsService}              from '../../../../../services/common/agriport-constants.service';
import {GetRoundNumericService}                from '../../../../../ap-utils/service/get-round-numeric.service';
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;
import IDriver = Data.DocuContext.Driver.IDriver;
import IWorktypes = Data.BaseData.IWorktypes;
import IPlantProtectionProduct = Data.BaseData.IPlantProtectionProduct;
import { ApDateMinMaxValidator } from 'src/app/ap-dynforms/validators/ap-date-min-max-validator';
import { LanguageStore } from 'src/app/stores/translation/language.store';

@Injectable()
export class DetailsCommonUiProvider {
  private static readonly ProductAvgLabelBase = 'Global__Product_avg';
  private static readonly ProductMaxLabelBase = 'MaxRateAtMaxWater';

  constructor(private driverStore: DriverStore,
              private machineStore: MachineStore,
              private instrumentStore: InstrumentStore,
              private campaignYearStore: CampaignYearStore,
              private dateService: ApDateService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private agriportConstantsService: AgriportConstantsService,
              private languageStore: LanguageStore) {
  }

  /**
   * Creates a fieldset for the documentation section of the details form
   * Includes combobox for selecting machine, instrument, and driver
   * @returns Fieldset configuration for the documentation section
   * @public
   */
  public createGeneralFieldset(operationModeEnum: OperationModesEnum,
                               operationModeAppSettings: IOperationModeAppSetting[],
                               validWorkTypes: IWorktypes[]): ApDynformsConfigFieldset[] {
    const result: ApDynformsConfigFieldset[] = [];
    const selectedYear = this.campaignYearStore.getSelectedCampaignYear();
    result.Add(new ApDynformsConfigFieldset({
      key: 'Information',
      legend: 'Global__Information',
      config: [
        new ApDynformsConfigTextbox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('CountFields'),
          label: 'AgriConnect__Count_Fields',
          informational: true
        }),
        new ApDynformsConfigTextbox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('TotalArea'),
          label: 'Nutrients__TotalArea_ha',
          informational: true
        }),
        new ApDynformsConfigTextbox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('Crop'),
          label: 'Settings__Lbl_Menu_Croptypes',
          informational: true
        })
      ]
    }));
    result.Add(new ApDynformsConfigFieldset({
      key: 'General',
      legend: 'Global__General_Information',
      config: [
        new ApDynformsConfigComboBox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('OperationModeKey'),
          label: 'Global__OperationMode',
          options: operationModeAppSettings,
          valueField: nameOf<IOperationModeAppSetting>('OperationMode'),
          textField: nameOf<IOperationModeAppSetting>('Text'),
          groupByField: nameOf<IOperationModeAppSetting>('Category'),
          translate: true,
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
          ]
        }),
        new ApDynformsConfigComboBox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('WorkType'),
          label: 'Docu_Ini__WorkType',
          options: validWorkTypes,
          disabled: operationModeEnum !== OperationModesEnum.Constant,
          valueField: nameOf<IWorktypes>('Id'),
          textField: nameOf<IWorktypes>('Description'),
          translate: true,
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
          ]
        }),
        new ApDynformsConfigDatepicker({
          key: nameOf<IPpPlanningWizardDetailsFormData>('ApplicationDate'),
          label: 'Nutrients__FertilizationDate',
          minDate: new Date(this.campaignYearStore.getSelectedCampaignYear().DefaultStart),
          maxDate: this.dateService.getEndOfDayDate(this.campaignYearStore.getSelectedCampaignYear().DefaultEnd),
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            new ApDynformsValidator({
              validator: ApDateMinMaxValidator.ValidateDateRange(
                this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultStart)),
                this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultEnd).add(1, 'days'))),
              errorKey: ApDateMinMaxValidator.getYearRangeFormatted(this.languageStore.CurrentDateFormat, 
                this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultStart)),
                this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultEnd)))
            }),
          ]
        }),
        new ApDynformsConfigTextbox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('Note'),
          label: 'Global__Note'
        })
      ]
    }));
    return result;
  }

  /**
   * Creates a fieldset for the product selection section of the details form
   * Includes a combobox for selecting a product, numeric inputs for amount and max amount,
   * and a button for adding the selected product to the list
   * @param productsTemplateRef Template reference for the product list
   * @param responsiveData Responsive data related to the product selection
   * @param addProductCallback Callback for adding a product to the list
   * @returns Array of fieldset configurations
   * @public
   */
  public createProductsFieldset(productsTemplateRef: TemplateRef<any>,
                                responsiveData: IPpPlanningWizardDetailsResponsiveData,
                                addProductCallback: EventEmitter<any>): ApDynformsConfigFieldset[] {
    const productsFieldset: ApDynformsConfigFieldset[] = [];
    productsFieldset.Add(new ApDynformsConfigFieldset({
      legend: 'Global__Products',
      columns: 1,
      useMaxWidth: true,
      key: 'Global__Products',
      infoText: `${this.translationService.translate('Global__Maximum')} ${PpConstants.MAX_SELECTABLE_PRODUCTS}`,
      config: [
        new ApDynformsConfigTemplate({
          key: 'SelectedProducts',
          value: productsTemplateRef
        })
      ]
    }));
    productsFieldset.Add(new ApDynformsConfigFieldset({
      key: 'ProductSelection',
      legend: 'ProductSelection',
      hideLegend: true,
      classList: 'ap-field-set-no-legend-line',
      config: [
        new ApDynformsConfigComboBox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('PreProductId'),
          label: 'Global__Product',
          // we need the rxjs delay here to put the update of the disabled value on top of our callstack
          // if it's updated immediately the form still shows its old value because clearing the values happens later (formValueChanges).
          disabled$: responsiveData.ProductListItems$?.pipe(
            delay(0),
            map(p => p?.length >= PpConstants.MAX_SELECTABLE_PRODUCTS)
          ),
          options: responsiveData.PreselectValidProducts$.pipe(
            map(products => this.sortProductsByName(products)),
            map(products => this.getProductOption(products))
          ),
          valueField: nameOf<IProductOption>('Id'),
          textField: nameOf<IProductOption>('Name'),
          groupByField: nameOf<IProductOption>('EffectiveRange'),
          translate: true
        }),
        new ApDynformsConfigPlaceholder(),
        new ApDynformsConfigNumerictextbox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('PreAvgAmount'),
          // we need the rxjs delay here to put the update of the disabled value on top of our callstack
          // if it's updated immediately the form still shows its old value because clearing the values happens later (formValueChanges).
          disabled$: combineLatest([
            responsiveData.PreselectedProductDetail$,
            responsiveData.ProductListItems$
          ]).pipe(
            delay(0),
            map(([preselectedProductDetails, productListItems]) => !preselectedProductDetails || productListItems?.length >= PpConstants.MAX_SELECTABLE_PRODUCTS)
          ),
          min: responsiveData.PreselectedMinProductRate$,
          max: responsiveData.PreselectedMaxProductRate$,
          label$: responsiveData.PreselectedProductDetail$.pipe(map(d =>
            this.getLabelWithUnitAndMinMaxRange(DetailsCommonUiProvider.ProductAvgLabelBase, d?.Unit?.Key))),
          decimals: 3
        }),
        new ApDynformsConfigNumerictextbox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('PreMaxAmount'),
          infoText: 'InformationMaxRateAtMaxWater',
          disabled: true,
          label$: responsiveData.PreselectedProductDetail$.pipe(
            map(productDetail => {
              const minProductOfProductMax = +this.agriportConstantsService.GetConstant(AgriportConstantsEnum.PpMinProductOfProductMax);
              return this.getLabelWithUnitAndMinMaxRange(DetailsCommonUiProvider.ProductMaxLabelBase,
                productDetail?.Unit?.Key, productDetail?.AmountMax * minProductOfProductMax, productDetail?.AmountMax);
            })
          ),
          decimals: 3
        })
      ]
    }));

    productsFieldset.Add(new ApDynformsConfigFieldset({
        key: 'AddProduct',
        legend: 'AddProduct',
        hideLegend: true,
        classList: 'ap-field-set-no-legend-line',
        columns: 3,
        config: [
          new ApDynformsConfigPlaceholder(),
          new ApDynformsConfigButton({
            key: 'addProductButtonKey',
            value: 'Global_Add',
            callback: addProductCallback,
            disabledButton$: responsiveData?.AddProductButtonDisabled$
          }),
          new ApDynformsConfigPlaceholder()
        ]
      }
    ));
    return productsFieldset;
  }

  /**
   * Creates a configuration fieldset for the document settings in the planning wizard.å
   * @param data - The initial data used to populate the fieldset.
   * @return The configured dynamic forms fieldset for document settings.
   * @public
   */
  public createDocuFieldSet(data: IPpPlanningWizardDetailsFormData | undefined): ApDynformsConfigFieldset {
    return new ApDynformsConfigFieldset({
      key: 'Docu',
      legend: 'Settings__Lbl_Menu_Resources',
      config: [
        new ApDynformsConfigComboBox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('Machine'),
          label: 'Settings__Lbl_Menu_Devices',
          textField: nameOf<IMachine>('NameWithSign'),
          valueField: nameOf<IMachine>('Id'),
          options: this.machineStore.Machines$,
          value: data?.Machine,
          valuePrimitive: true,
          sort: 1
        }),
        new ApDynformsConfigComboBox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('Instrument'),
          label: 'Docu_Ini__Tool',
          textField: nameOf<IInstrument>('Name'),
          valueField: nameOf<IInstrument>('Id'),
          options: this.instrumentStore.Instruments$,
          value: data?.Instrument,
          valuePrimitive: true,
          sort: 1
        }),
        new ApDynformsConfigComboBox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('Driver'),
          label: 'Docu_Ini__Motorist',
          textField: nameOf<IDriver>('FirstNameFullName'),
          valueField: nameOf<IDriver>('Id'),
          options: this.driverStore.Drivers$,
          value: data?.Driver,
          valuePrimitive: true,
          sort: 1
        }),
        new ApDynformsConfigNumerictextbox({
          key: nameOf<IPpPlanningWizardDetailsFormData>('HaPerHour'),
          label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
          value: data?.HaPerHour,
          decimals: 1,
          min: 0,
          step: 0.1
        })
      ]
    });
  }

  /**
   * Converts an array of plant protection products to an array of product options
   * @param products Array of plant protection products
   * @returns Array of product options
   * @private
   */
  private getProductOption(products: IPlantProtectionProduct[]): IProductOption[] {
    return products.map(product => {
      const productDetail = product?.ProductDetails?.FirstOrDefault();
      return {
        Id: product.Id,
        Name: product.ProductName,
        EffectiveRange: productDetail?.EffectiveRange?.Key
      };
    });
  }

  /**
   * Sorts an array of plant protection products by their name
   * @param products Array of plant protection products
   * @returns Sorted an array of plant protection products
   * @private
   */
  private sortProductsByName(products: IPlantProtectionProduct[]): IPlantProtectionProduct[] {
    return products.sort((first, second) => {
      if (first.ProductName == null) {
        return 1;
      }
      if (second.ProductName == null) {
        return -1;
      }
      return first.ProductName.localeCompare(second.ProductName);
    });
  }

  /**
   * Generates a label for the product and max amount inputs
   * Includes the base label, the maximum allowed amount, and the unit
   * @param baseLabel The base label for the input
   * @param unitKey Translation key for the product's unit of measurement
   * @param minAmount Optional minimum allowed amount for the product
   * @param maxAmount Optional maximum allowed amount for the product
   * @returns The formatted label
   * @private
   */
  private getLabelWithUnitAndMinMaxRange(baseLabel: string, unitKey: string | undefined,
                                         minAmount?: number, maxAmount?: number): string {
    if (!unitKey) {
      return baseLabel;
    }
    let label = `${this.translationService.translate(baseLabel)}`;
    if (Number.isFinite(minAmount) || Number.isFinite(maxAmount)) {
      minAmount = this.roundNumericService.roundAsNumber(minAmount, 3);
      maxAmount = this.roundNumericService.roundAsNumber(maxAmount, 3);
      label += ` [${this.translationService.translate('RangeAllowed')} ${minAmount} - ${maxAmount}`;
    }
    return label + ` ${this.translationService.translate(unitKey)}]`;
  }
}
